<template>
  <hel-module title="近 6 个月签约与回款趋势" :class="scope.clsPrefix" v-loading="loading">
    <template slot="extra">
      <span class="chart-unit">单位：万元</span>
    </template>

    <div class="chart-subtitle">
      柱状为签约额和回款额，折线为目标值；数据按财务到账口径统计
    </div>
    <div class="chart-container">
      <div id="salesSignPaymentTrendChart" class="chart-body"></div>
      <div class="chart-source">
        <i class="chart-source-icon"></i>
        数据来源：合同系统 + 财务系统 + 目标管理
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import * as echarts from 'echarts'
import { getSalesSignPaymentTrend } from '@/api/sales'

export default {
  name: 'SalesSignPaymentTrend',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-sales-sign-payment-trend'
      },
      months: [],
      signAmounts: [],
      paymentAmounts: [],
      targets: [],
      chartInstance: null
    }
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchTrendData() {
      const res = await getSalesSignPaymentTrend()
      if (!res || res.code !== 200 || !Array.isArray(res.data)) {
        return []
      }
      return res.data
    },
    async fetchData() {
      this.loading = true
      try {
        const data = await this.fetchTrendData()
        if (!Array.isArray(data) || !data.length) {
          this.months = []
          this.signAmounts = []
          this.paymentAmounts = []
          this.targets = []
          return
        }
        this.months = data.map((i) => {
          // '2026-05' → '5月'
          const parts = i.statMonth ? i.statMonth.split('-') : []
          return parts.length === 2 ? String(Number(parts[1])) + '月' : i.statMonth
        })
        this.signAmounts = data.map((i) => Number(i.signAmount) || 0)
        this.paymentAmounts = data.map((i) => Number(i.paymentAmount) || 0)
        this.targets = data.map((i) => Number(i.target) || 0)

        this.$nextTick(() => {
          this.drawChart()
        })
      } catch (error) {
        console.error('获取近6个月签约与回款趋势数据失败:', error)
        this.months = []
        this.signAmounts = []
        this.paymentAmounts = []
        this.targets = []
      } finally {
        this.loading = false
      }
    },
    drawChart() {
      const chart = echarts.init(
        document.getElementById('salesSignPaymentTrendChart')
      )
      const option = {
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const lines = params.map(
              (p) => `${p.marker}${p.seriesName}: ${p.value}万`
            )
            return [params[0].axisValue, ...lines].join('<br/>')
          }
        },
        legend: {
          top: 0,
          right: 0,
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 16,
          icon: 'roundRect',
          textStyle: {
            color: '#606266',
            fontSize: 12
          },
          data: ['签约额', '回款额', '月度目标']
        },
        grid: {
          top: 34,
          left: 10,
          right: 10,
          bottom: 10,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.months,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed'
            }
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12
          }
        },
        series: [
          {
            name: '签约额',
            type: 'bar',
            barWidth: 14,
            barGap: '30%',
            itemStyle: {
              color: '#4a64f4',
              borderRadius: [4, 4, 0, 0]
            },
            data: this.signAmounts
          },
          {
            name: '回款额',
            type: 'bar',
            barWidth: 14,
            itemStyle: {
              color: '#7da6f8',
              borderRadius: [4, 4, 0, 0]
            },
            data: this.paymentAmounts
          },
          {
            name: '月度目标',
            type: 'line',
            step: 'middle',
            symbol: 'none',
            lineStyle: {
              color: '#e5a23d',
              width: 2
            },
            itemStyle: {
              color: '#e5a23d'
            },
            data: this.targets
          }
        ]
      }
      chart.setOption(option)
      this.chartInstance = chart
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartInstance && this.chartInstance.resize()
        })
        setTimeout(() => {
          this.chartInstance && this.chartInstance.resize()
        }, 200)
      })
    },
    handleResize() {
      if (this.chartInstance) {
        setTimeout(() => {
          this.chartInstance.resize()
        }, 200)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-sales-sign-payment-trend";
.#{$class-prefix} {
  .chart-unit {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    white-space: nowrap;
  }
  .chart-subtitle {
    font-size: 12px;
    font-family: PingFangSC-Regular;
    color: #909399;
    margin-bottom: 4px;
  }
  .chart-container {
    height: calc(100% - 22px);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .chart-body {
    flex: 1;
    width: 100%;
    min-height: 240px;
  }
  .chart-source {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: PingFangSC-Regular;
    font-size: 12px;
    color: #999999;
    font-weight: 400;
    margin-top: 4px;
  }
  .chart-source-icon {
    width: 12px;
    height: 12px;
    border: 1px solid #b6c2d9;
    border-radius: 50%;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 2px;
      height: 2px;
      border-radius: 50%;
      background: #b6c2d9;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
